import {
  Grid,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Checkbox,
  Box,
  Flex,
  Select,
  Button,
  VStack,
  useDisclosure,
  Collapse,
  Text,
} from '@chakra-ui/react';

import DateField from 'components/fields/DateField';
import {
  parseDateString,
  formatDateToString,
} from 'components/fields/auto/resolvers/resolverUtils';
import { PROVINCE_ID_TO_CODE } from 'variables/constants';
import { useAnswerCodes, useQuoteLock, useValidationSchema } from '../../hooks';
import ValidatedDateField from '../../components/ValidatedDateField';
import type { DriverLicensingProps } from './types';

const emptyOtherLicense = {
  motorcycle: false,
  motorcycle_class: '',
  commercial: false,
  commercial_class: '',
  physical_impairment: false,
};

const DriverLicensing = ({
  driver,
  index,
  quoteProvince,
  effectiveDate,
  onNestedChange,
}: DriverLicensingProps) => {
  const licensing = driver.licensing;
  const otherLicense = licensing?.other_license_details ?? emptyOtherLicense;
  const { isOpen: isOtherOpen, onToggle: onOtherToggle } = useDisclosure();
  const { isLocked } = useQuoteLock();
  const { getLicenseConfig } = useAnswerCodes();
  const { isFieldRequired } = useValidationSchema();

  const isRequired = (field: string) =>
    isFieldRequired(
      'driver',
      field,
      driver as unknown as Record<string, unknown>,
    );

  const provinceCode = PROVINCE_ID_TO_CODE[quoteProvince] || 'ON';
  const licenseConfig = getLicenseConfig(provinceCode);

  const calculateAge = (dob: string | null): number | null => {
    if (!dob) return null;
    const today = new Date();
    const birthDate = parseDateString(dob);
    if (!birthDate) return null;
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    return age;
  };

  const calculateYearsOrMonths = (
    startDate: string | null,
    endDate: string,
  ): { years: number | null; months: number | null } => {
    if (!startDate || !endDate) return { years: null, months: null };
    const end = parseDateString(endDate);
    const start = parseDateString(startDate);
    if (!start || !end) return { years: null, months: null };
    const totalMonths =
      (end.getFullYear() - start.getFullYear()) * 12 +
      (end.getMonth() - start.getMonth());
    return {
      years: Math.floor(totalMonths / 12),
      months: Math.max(0, totalMonths),
    };
  };

  const getEndDateForField = (field: string): string => {
    if (field.includes('g1') && licensing?.class_g2_license_date) {
      return licensing.class_g2_license_date as string;
    }
    if (field.includes('g2') && licensing?.class_g_license_date) {
      return licensing.class_g_license_date as string;
    }
    return effectiveDate;
  };

  const age = calculateAge(licensing?.date_of_birth ?? null);

  const getLicenseDateValue = (field: string): string | null => {
    return (licensing as Record<string, any>)[field] ?? null;
  };

  const setLicenseDateValue = (field: string, value: string | null) => {
    onNestedChange(index, 'licensing', field, value);
  };

  const hasAnyLicenseDate = licenseConfig?.date_fields.some(
    (df) => !!getLicenseDateValue(df.field),
  );
  const showLicenseDateError =
    licenseConfig && licenseConfig.date_fields.length > 0 && !hasAnyLicenseDate;

  return (
    <VStack spacing={2} align="stretch">
      <Grid templateColumns="1fr 80px" gap={1}>
        <DateField
          label="Date Of Birth"
          date={parseDateString(licensing?.date_of_birth ?? null)}
          onChange={(date: Date | null) =>
            onNestedChange(
              index,
              'licensing',
              'date_of_birth',
              formatDateToString(date),
            )
          }
          isDisabled={false}
          size="sm"
          required
        />
        <FormControl>
          <FormLabel fontSize="xs" whiteSpace="nowrap">
            Age
          </FormLabel>
          <Input
            variant="main"
            size="sm"
            value={age !== null ? age : ''}
            isReadOnly
            bg="gray.50"
          />
        </FormControl>
      </Grid>

      {licenseConfig && licenseConfig.date_fields.length > 0 && (
        <Text
          fontSize="xs"
          color={showLicenseDateError ? 'red.600' : 'gray.500'}
          mb={1}
        >
          * At least one license date is required
        </Text>
      )}

      {licenseConfig?.date_fields.map((dateField) => {
        const dateValue = getLicenseDateValue(dateField.field);
        const endDate = getEndDateForField(dateField.field);
        const { years, months } = calculateYearsOrMonths(dateValue, endDate);
        const isGraduatedLicense =
          dateField.field.includes('g1') || dateField.field.includes('g2');
        const showYears = !isGraduatedLicense && years !== null && years >= 1;

        return (
          <Grid
            key={dateField.field}
            templateColumns="1fr 60px"
            gap={1}
            alignItems="end"
          >
            <ValidatedDateField
              itemPath="driver"
              fieldPath={`licensing.${dateField.field}`}
              label={dateField.label}
              date={parseDateString(dateValue)}
              onChange={(date: Date | null) =>
                setLicenseDateValue(dateField.field, formatDateToString(date))
              }
              entity={driver as unknown as Record<string, unknown>}
              isDisabled={false}
            />
            <FormControl>
              <FormLabel fontSize="xs" whiteSpace="nowrap">
                {showYears ? 'Years' : 'Months'}
              </FormLabel>
              <Input
                variant="main"
                size="sm"
                value={showYears ? years : (months ?? '')}
                isReadOnly
                bg="gray.50"
              />
            </FormControl>
          </Grid>
        );
      })}

      {!licenseConfig && (
        <FormHelperText fontSize="xs" color="orange.500">
          License configuration not available for this province
        </FormHelperText>
      )}

      <Checkbox
        isChecked={licensing?.out_of_province_country ?? false}
        onChange={(e) =>
          onNestedChange(
            index,
            'licensing',
            'out_of_province_country',
            e.target.checked,
          )
        }
        isDisabled={false}
        size="sm"
      >
        Out Of Province/Country Driver
      </Checkbox>

      <Flex align="center" gap={1}>
        <Checkbox
          isChecked={licensing?.drivers_training ?? false}
          onChange={(e) =>
            onNestedChange(
              index,
              'licensing',
              'drivers_training',
              e.target.checked,
            )
          }
          isDisabled={false}
          size="sm"
        >
          Drivers Training
        </Checkbox>
      </Flex>

      {licensing?.drivers_training && (
        <DateField
          label="Date Completed"
          date={parseDateString(licensing?.training_completion_date ?? null)}
          onChange={(date: Date | null) =>
            onNestedChange(
              index,
              'licensing',
              'training_completion_date',
              formatDateToString(date),
            )
          }
          isDisabled={false}
          size="sm"
          required={isRequired('licensing.training_completion_date')}
        />
      )}

      <Button
        size="sm"
        variant="outline"
        onClick={onOtherToggle}
        colorScheme="blue"
      >
        Other Licence Details
      </Button>

      <Collapse in={isOtherOpen || isLocked}>
        <Box p={3} borderWidth="1px" borderRadius="md" bg="gray.50">
          <VStack spacing={2} align="stretch">
            <Flex direction="column" gap={2}>
              <Checkbox
                isChecked={otherLicense.motorcycle}
                onChange={(e) =>
                  onNestedChange(
                    index,
                    'licensing',
                    'other_license_details',
                    e.target.checked,
                    'motorcycle',
                  )
                }
                isDisabled={false}
                size="sm"
              >
                Motorcycle License
              </Checkbox>
              {otherLicense.motorcycle && licenseConfig?.motorcycle && (
                <>
                  <FormControl>
                    <FormLabel fontSize="xs">Motorcycle Class</FormLabel>
                    <Select
                      variant="main"
                      size="sm"
                      value={otherLicense.motorcycle_class || ''}
                      onChange={(e) =>
                        onNestedChange(
                          index,
                          'licensing',
                          'other_license_details',
                          e.target.value || null,
                          'motorcycle_class',
                        )
                      }
                      isDisabled={false}
                    >
                      {licenseConfig.motorcycle.classes.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                  {licenseConfig.motorcycle.date_fields.map((dateField) => {
                    const motorcycleClass = otherLicense.motorcycle_class || '';
                    const isAllowed =
                      !dateField.allowed_class_codes?.length ||
                      dateField.allowed_class_codes.includes(motorcycleClass);

                    if (!isAllowed) return null;

                    return (
                      <DateField
                        key={dateField.field}
                        label={dateField.label}
                        date={parseDateString(
                          (otherLicense as Record<string, any>)[
                            dateField.field
                          ] ?? null,
                        )}
                        onChange={(date: Date | null) =>
                          onNestedChange(
                            index,
                            'licensing',
                            'other_license_details',
                            formatDateToString(date),
                            dateField.field,
                          )
                        }
                        isDisabled={false}
                        size="sm"
                      />
                    );
                  })}
                </>
              )}
            </Flex>
            <Flex direction="column" gap={1}>
              <Checkbox
                isChecked={otherLicense.commercial}
                onChange={(e) =>
                  onNestedChange(
                    index,
                    'licensing',
                    'other_license_details',
                    e.target.checked,
                    'commercial',
                  )
                }
                isDisabled={false}
                size="sm"
              >
                Commercial License
              </Checkbox>
              {otherLicense.commercial &&
                licenseConfig?.commercial_classes &&
                licenseConfig.commercial_classes.length > 0 && (
                  <FormControl>
                    <FormLabel fontSize="xs" whiteSpace="nowrap">
                      Commercial Class
                    </FormLabel>
                    <Select
                      variant="main"
                      size="sm"
                      value={otherLicense.commercial_class || ''}
                      onChange={(e) =>
                        onNestedChange(
                          index,
                          'licensing',
                          'other_license_details',
                          e.target.value || null,
                          'commercial_class',
                        )
                      }
                      isDisabled={false}
                    >
                      {licenseConfig.commercial_classes.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                )}
            </Flex>
            <Checkbox
              isChecked={otherLicense.physical_impairment}
              onChange={(e) =>
                onNestedChange(
                  index,
                  'licensing',
                  'other_license_details',
                  e.target.checked,
                  'physical_impairment',
                )
              }
              isDisabled={false}
              size="sm"
            >
              Physical Impairment
            </Checkbox>
          </VStack>
        </Box>
      </Collapse>
    </VStack>
  );
};

export default DriverLicensing;
